import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

/**
 * El asistente IA del store en xcon-api (change conversaciones-agente-ia, D33 y D46). Leer pide
 * `store_operators` Viewer y guardar, Admin: el mismo gate que Operadores del Store.
 */
const agentBase = (storeCode: string) =>
  `${API_BASE_URL}/stores/${encodeURIComponent(storeCode)}`;

export const AGENT_PROVIDERS = ["anthropic", "openai", "google"] as const;
export type AgentProvider = (typeof AGENT_PROVIDERS)[number];

export interface AgentLimits {
  maxToolCallsPerTurn: number | null;
  maxTokensPerDay: number | null;
  maxTurnsPerConversation: number | null;
  ratePerMin: number | null;
}

export interface AgentFuse {
  userId: number;
  disabled: boolean;
  disabledUntil: string | null;
  trippedAt: string | null;
  reason: string | null;
}

export interface AgentConfig {
  storeCode: string;
  configured: boolean;
  enabled: boolean;
  enabledAt?: string | null;
  displayName?: string | null;
  provider?: string | null;
  model?: string | null;
  hasApiKey: boolean;
  /** `••••abcd`: la key no vuelve nunca. */
  apiKeyHint: string | null;
  preferredSeller?: string | null;
  limits?: AgentLimits;
  knowledgeVersion?: number;
  fuse: AgentFuse[];
  defaults: {
    ratePerMin: number;
    fuseRequestsPerMin: number;
    fuseErrorsPerMin: number;
    fuseMinutes: number;
  };
  updatedBy?: number | null;
  updatedAt?: string | null;
}

/** Lo que no viene no se toca; `apiKey: null` la borra y `null` en un límite vuelve al default. */
export interface UpdateAgentConfig extends Partial<AgentLimits> {
  enabled?: boolean;
  displayName?: string;
  provider?: string;
  model?: string;
  apiKey?: string | null;
  preferredSeller?: string | null;
}

export interface AgentBudget {
  day: string;
  timezone: string;
  tokensToday: number;
  maxTokensPerDay: number | null;
  exhausted: boolean;
  resetsAt: string;
}

export interface AgentStatus {
  storeCode: string;
  online: boolean;
  lastSeenAt: string | null;
  baseVersion: string | null;
  startedAt: string | null;
  /** El usuario con el que corre el pod: el que se ofrece como asistente del canal. */
  agentUserId: number | null;
  fuse: AgentFuse[];
  budget: AgentBudget;
}

export interface AgentRunsByModel {
  provider: string | null;
  model: string | null;
  runs: number;
  tokensIn: number;
  tokensOut: number;
  tokensCached: number;
  costEstimate: number;
}

export interface AgentRunsSummary {
  storeCode: string;
  month: string;
  timezone: string;
  totals: Omit<AgentRunsByModel, "provider" | "model">;
  byModel: AgentRunsByModel[];
}

export const KNOWLEDGE_SECTIONS = [
  "negocio",
  "presupuestar",
  "recetas",
  "faq",
  "no_hacer",
  "tono",
] as const;
export type KnowledgeSection = (typeof KNOWLEDGE_SECTIONS)[number];
export type Knowledge = Partial<Record<KnowledgeSection, string>> & {
  /** D58: «Notas de la sucursal», por código de seller. Lo que su config no dice. */
  branchNotes?: Record<string, string>;
};

export interface KnowledgeVersion {
  version: number;
  restoredFrom: number | null;
  updatedBy: number | null;
  createdAt: string | null;
}

export interface AgentKnowledge {
  storeCode: string;
  knowledge: Knowledge;
  version: number;
  sections: { key: KnowledgeSection; maxLength: number }[];
  versions: KnowledgeVersion[];
}

export const agentApi = {
  config: (storeCode: string) =>
    axiosInstance
      .get<AgentConfig>(`${agentBase(storeCode)}/agent-config`)
      .then((r) => r.data),

  guardar: (storeCode: string, body: UpdateAgentConfig) =>
    axiosInstance
      .put<AgentConfig>(`${agentBase(storeCode)}/agent-config`, body)
      .then((r) => r.data),

  levantarFusible: (storeCode: string) =>
    axiosInstance
      .put<{ reset: unknown; fuse: AgentFuse[] }>(
        `${agentBase(storeCode)}/agent-config/reset-fuse`,
      )
      .then((r) => r.data),

  estado: (storeCode: string) =>
    axiosInstance
      .get<AgentStatus>(`${agentBase(storeCode)}/agent-status`)
      .then((r) => r.data),

  resumen: (storeCode: string, month?: string) =>
    axiosInstance
      .get<AgentRunsSummary>(`${agentBase(storeCode)}/agent-runs/summary`, {
        params: month ? { month } : {},
      })
      .then((r) => r.data),

  conocimiento: (storeCode: string) =>
    axiosInstance
      .get<AgentKnowledge>(`${agentBase(storeCode)}/agent-config/knowledge`)
      .then((r) => r.data),

  guardarConocimiento: (storeCode: string, knowledge: Knowledge) =>
    axiosInstance
      .put<AgentKnowledge>(
        `${agentBase(storeCode)}/agent-config/knowledge`,
        knowledge,
      )
      .then((r) => r.data),

  restaurar: (storeCode: string, version: number) =>
    axiosInstance
      .post<AgentKnowledge>(
        `${agentBase(storeCode)}/agent-config/knowledge/restore/${version}`,
      )
      .then((r) => r.data),
};
